import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isNativeNotWeb } from '@/utils/platform-check';

/**
 * Capgo live-update (OTA) wrapper — the whole plugin surface lives here.
 *
 * 1. `notifyOtaAppReady()` — tells Capgo the bundle booted. Miss it and Capgo
 *    reverts to the previous bundle, so it runs as early as possible and never
 *    depends on the network. See the placement note in `src/main.js`.
 *    `resignalOtaAppReady()` repeats the signal on every resume, because the
 *    plugin waits for it again after every foreground check — see that function.
 * 2. `syncOtaChannel()` / `clearOtaChannel()` — channel assignment, which is
 *    also the gate on downloading at all: the backend offers an unassigned
 *    device nothing, so assignment happens only once a member is signed in.
 * 3. `getOtaBundleVersion()` — which bundle is running, for analytics and the
 *    force-update check.
 * 4. `reapStaleOtaBundles()` — hygiene. Deletes bundle records the plugin can
 *    neither finish nor install, which otherwise wedge a device permanently.
 * 5. `applyOtaKillSwitch()` — the remote off-switch. An explicit `false` on the
 *    `ota_enabled` flag unassigns the device and resets it to the bundle inside
 *    the binary.
 *
 *    Best-effort, and only against honest bundles: the code running the check is
 *    the code being killed, so a bundle broken enough to fail the flags fetch
 *    never reverts, and a malicious one simply omits the check. The dependable
 *    stop is a channel rollback on the Capgo side.
 *
 * Installing is deliberately *not* here: `autoUpdate` in capacitor.config.json
 * leaves that to the plugin, so a bundle cannot strand a device by shipping
 * without the code that installs the next one.
 *
 * Everything is a no-op on web and nothing throws — a failure in the update
 * layer must not be able to stop the app from starting.
 */

/**
 * Feature flag that gates live updates.
 *
 * Read in two different ways, deliberately:
 *
 * - `syncOtaChannel()` requires an explicit `true`. Channel assignment is the
 *   download gate, so an absent or false flag means the device is never offered
 *   a bundle. Off by default, like every other new feature here.
 * - `applyOtaKillSwitch()` acts only on an explicit `false`. Reverting is
 *   destructive and fleet-wide, so an outage of the flags API must not trigger it.
 *
 * The asymmetry is the point: "we cannot reach the flags API" should stop new
 * bundles arriving without tearing down the ones already installed.
 */
export const OTA_FEATURE_FLAG = 'ota_enabled';

/** Capgo's id for the bundle compiled into the store binary. */
const BUILTIN_BUNDLE_ID = 'builtin';

/** Remembers the channel we last assigned, so a launch costs no round trip. */
const CHANNEL_SYNCED_KEY = 'ota_channel_synced';

/**
 * Set when the kill switch has fired, and the reason it sticks.
 *
 * Reverting alone does not hold: the reverted bundle boots, the auth token
 * arrives, the token watcher re-assigns a channel, and the plugin downloads and
 * installs the killed bundle again — reverting once more on the launch after
 * that. The fleet then alternates every session, a hundred megabytes a time,
 * during whatever incident made us pull the switch. This marker is what stops
 * re-subscription, and it survives the reload because the reload is a fresh JS
 * context.
 *
 * It stores the *native version* that was running when the switch fired, not a
 * boolean, so a store release lifts it automatically. A boolean would outlive
 * its purpose: flag hygiene says delete a flag once it has served its turn, and
 * a device that never launched during the window where the flag read `true`
 * would keep a permanent marker — silently barred from every future bundle, on
 * every future binary, recoverable only by reinstalling. Dormant members are
 * exactly the ones that would hit it.
 */
const KILLED_KEY = 'ota_killed_at_native_version';

let readyNotified = false;
let bundleLookup = null;

/**
 * The binary's own version. Read directly rather than through app-version.js,
 * which imports this module — going the other way would be circular.
 *
 * Returns an empty string if the bridge is unavailable, which makes the kill
 * switch fail *open* in both directions: a kill recorded during a failed read
 * does not stick, and a later failed read reads as "the binary changed" and
 * lifts the block. Both are transient and self-correct, because the switch
 * re-fires on every launch while the flag reads false — and both fail the same
 * way as the localStorage guard below, deliberately. The dependable stop has
 * always been the Capgo channel rollback, not this.
 */
async function getAppVersionForOta() {
  try {
    const info = await App.getInfo();
    return { nativeVersion: info?.version || '' };
  } catch {
    return { nativeVersion: '' };
  }
}

/**
 * Report an OTA problem to Datadog RUM as well as the console.
 *
 * Every failure in this file used to be a `console.warn` and nothing else, on a
 * store build whose WebView cannot be inspected (`isInspectable` is not set, and
 * iOS 16.4+ requires it). So the one failure that matters — a device that never
 * gets onto its channel, and is therefore offered no bundle at all — was visible
 * to nobody. It cost an afternoon to find one such device by hand.
 *
 * Imported DYNAMICALLY to break a cycle: datadog.js pulls in app-version.js,
 * which imports this module. A static import here would evaluate that cycle on
 * the boot path, where this file is loaded first and deliberately depends on
 * almost nothing.
 *
 * Never throws and never blocks: reporting a problem must not become one.
 */
export async function reportOtaProblem(message, context = {}) {
  try {
    const { addDatadogRumError } = await import('@/utils/datadog');
    addDatadogRumError(new Error(message), { source: 'ota', ...context });
  } catch {
    // RUM unavailable or not yet initialised — the console warning at the call
    // site is the record.
  }
}

/**
 * Tag this device in Capgo with the member using it.
 *
 * Without this every device in the Capgo console is an opaque UUID, so
 * "why is this member not updating?" has no starting point — you are reduced to
 * guessing from last-seen timestamps and bundle versions. That is exactly how
 * the 2026-09-02 investigation went.
 *
 * Set on sign-in rather than at boot, because the member id is what makes it
 * useful and it is not known before then. Independent of channel assignment on
 * purpose: a device that CANNOT be assigned is precisely the one somebody will
 * need to find later, so tagging must not sit behind the thing that failed.
 *
 * @param {number|string|null|undefined} memberId
 * @returns {Promise<boolean>} true when the id was set
 */
export async function setOtaCustomId(memberId) {
  if (!isOtaSupported() || memberId === null || memberId === undefined) return false;

  const customId = String(memberId);
  if (!customId) return false;

  try {
    await CapacitorUpdater.setCustomId({ customId });
    return true;
  } catch (error) {
    // Non-fatal: this is diagnostics, not delivery. A device that cannot be
    // tagged still updates perfectly well.
    console.warn('[OTA] Could not set the Capgo custom id:', error?.message || error);
    return false;
  }
}

/** OTA only exists inside the native shell. */
function isOtaSupported() {
  return isNativeNotWeb();
}

/**
 * Tell Capgo this bundle loaded successfully.
 *
 * Idempotent, and safe to call before anything else has initialised — it takes
 * no arguments and makes no network request. Resolves to true when the plugin
 * acknowledged, false when OTA is not applicable or the call failed.
 *
 * @returns {Promise<boolean>}
 */
export async function notifyOtaAppReady() {
  if (!isOtaSupported()) return false;
  if (readyNotified) return true;

  const acknowledged = await signalAppReady('[OTA] notifyAppReady failed:');
  if (acknowledged) readyNotified = true;
  return acknowledged;
}

/**
 * The one call both ready signals make. A failure here means Capgo may revert
 * on the next launch, so it is surfaced loudly in the console — but it never
 * propagates, because the app still works without it.
 */
async function signalAppReady(failureMessage) {
  try {
    await CapacitorUpdater.notifyAppReady();
    return true;
  } catch (error) {
    console.warn(failureMessage, error?.message || error);
    return false;
  }
}

/**
 * Repeat the ready signal after the app returns to the foreground.
 *
 * On iOS the plugin runs an update check on EVERY foreground, and at the end of
 * each one (`sendReadyToJs`) it waits on a semaphore for `notifyAppReady`, up to
 * `appReadyTimeout` (10 s in our config), before firing `appReady` and hiding the
 * splash it may have shown on background. `notifyOtaAppReady()` signals once per
 * JS lifetime, so every warm foreground used to wait the full 10 s and log
 * `Semaphore wait timed out after 10000ms` — 4,900 times a week across two
 * thirds of the iOS fleet (RUM, 2026-09-03). Nothing was broken by it unless the
 * splash was up, but the noise buried the real update failures.
 *
 * The plugin's `notifyAppReady` is idempotent (it marks the current bundle
 * SUCCESS and signals), so re-sending is safe and cheap. Deliberately a separate
 * function: `notifyOtaAppReady()` keeps its once-per-launch contract, which the
 * boot path relies on.
 *
 * @returns {Promise<boolean>} true when the plugin acknowledged
 */
export async function resignalOtaAppReady() {
  if (!isOtaSupported()) return false;
  return signalAppReady('[OTA] notifyAppReady on resume failed:');
}

/**
 * The bundle currently running. Resolved once per launch — including a null
 * result, so a device whose plugin cannot answer does not re-ask on every
 * version check, Me-page mount and force-update retry.
 *
 * @returns {Promise<{id: string, version: string, isBuiltin: boolean}|null>}
 */
async function getOtaBundle() {
  if (!isOtaSupported()) return null;
  if (bundleLookup) return bundleLookup;

  bundleLookup = (async () => {
    try {
      const bundle = (await CapacitorUpdater.current())?.bundle;
      if (!bundle?.id) return null;

      return {
        id: bundle.id,
        version: bundle.version || '',
        isBuiltin: bundle.id === BUILTIN_BUNDLE_ID,
      };
    } catch (error) {
      console.warn('[OTA] Could not read the current bundle:', error?.message || error);
      return null;
    }
  })();

  return bundleLookup;
}

/**
 * Version of the live-updated bundle, or null when the app is running the code
 * that shipped in the binary. Used to attribute errors and to tell the
 * force-update API which JS the device is actually on.
 *
 * @returns {Promise<string|null>}
 */
export async function getOtaBundleVersion() {
  const bundle = await getOtaBundle();
  if (!bundle || bundle.isBuiltin || !bundle.version) return null;
  return bundle.version;
}

/**
 * Delete bundle records the plugin can neither finish nor install.
 *
 * A download that dies mid-flight — the app force-quit, the process reaped in
 * the background — leaves a record in the plugin's store with status
 * `downloading` and no bundle folder on disk. Nothing reaps it: the plugin's
 * own `cleanupObsoleteVersions` runs only when the *native build* changes, and
 * it deletes orphaned folders, never orphaned records. What is left is a
 * deadlock exactly one bundle label wide:
 *
 * - the update check finds the record by version name and skips downloading
 *   entirely — the only branch that downloads is the one where no record
 *   exists or the record is marked deleted; and
 * - staging then refuses it, because that wants the folder and the
 *   `index.html` the interrupted download never wrote.
 *
 * So the device reports an update is available on every launch, fetches
 * nothing, installs nothing, and cannot recover until a bundle with a
 * different label is published. Observed on a store install of 1.2.12 (234)
 * that could not take `1.2.13-234.1`: `New bundle ... Update will occur now`
 * followed 46ms later by `Direct update failed to stage downloaded bundle`,
 * with no download in between, every single launch.
 *
 * The two list modes are what make this exact rather than a guess about
 * timestamps. `list()` enumerates folders on disk; `list({ raw: true })`
 * enumerates the record store. A record in the second and absent from the
 * first is an orphan by definition — a live download creates its folder
 * *before* it saves its record, so anything genuinely in flight appears in
 * both and is left alone.
 *
 * Orphans in `error` status are also left alone. The plugin keeps those on
 * purpose, to stop a bundle that failed to boot being downloaded again;
 * reaping them would trade this deadlock for a boot loop.
 *
 * @returns {Promise<number>} how many records were deleted
 */
export async function reapStaleOtaBundles() {
  if (!isOtaSupported()) return 0;

  let orphans = [];

  try {
    const [onDisk, stored] = await Promise.all([
      CapacitorUpdater.list(),
      CapacitorUpdater.list({ raw: true }),
    ]);

    const onDiskIds = new Set((onDisk?.bundles || []).map(bundle => bundle?.id));
    const running = await getOtaBundle();

    orphans = (stored?.bundles || []).filter(
      bundle =>
        bundle?.id &&
        bundle.id !== BUILTIN_BUNDLE_ID &&
        bundle.id !== running?.id &&
        bundle.status !== 'error' &&
        !onDiskIds.has(bundle.id),
    );
  } catch (error) {
    console.warn('[OTA] Could not read the bundle list:', error?.message || error);
    return 0;
  }

  // Sequential, and each delete guarded on its own: one record refusing to go
  // must not strand the rest, since any single orphan is enough to wedge the
  // label it holds.
  let reaped = 0;
  for (const bundle of orphans) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await CapacitorUpdater.delete({ id: bundle.id });
      reaped += 1;
      console.log(`[OTA] Reaped stale bundle record ${bundle.id} (${bundle.version})`);
    } catch (error) {
      console.warn(`[OTA] Could not reap stale bundle ${bundle.id}:`, error?.message || error);
    }
  }

  return reaped;
}

/**
 * Point this install at the channel its build belongs to.
 *
 * Called only once a member is signed in, and that is the whole gate on
 * downloading: an unassigned device is offered nothing by the backend, so no
 * bytes move before sign-in. The alternative — turning the plugin's auto-update
 * off and downloading from JS — would put installing back in the hands of the
 * bundle being replaced, which is the one failure mode worth designing around.
 *
 * **This depends on the Capgo default channel serving no bundle.** A device with
 * no assignment falls back to the default — a channel named `none` that exists
 * only because Capgo requires one to be default. Link a bundle to it and the
 * gate silently stops existing.
 *
 * Which channel a build subscribes to comes from `VUE_APP_OTA_CHANNEL`, set
 * explicitly per build. Requires "allow device self-assign" on the channel in
 * Capgo.
 *
 * What actually keeps a `dev` bundle off a member's phone is the channel's own
 * build targeting, not this value: every update request carries `is_prod` and
 * `is_emulator`, derived natively, so a `dev` channel set to serve non-prod
 * builds refuses a store install regardless of what it asks for. This is the
 * client saying where it belongs; the channel decides whether to believe it.
 *
 * Requires an explicit `true` on `ota_enabled`. Assignment is the download gate,
 * so gating it here is what makes live updates off-by-default in an environment
 * that has never created the flag row — which is how the feature has to ship,
 * and what the flag could not do while it was only a kill switch.
 *
 * @param {boolean|undefined} flagValue value of `ota_enabled`, or undefined
 * @returns {Promise<boolean>} true when the channel was changed
 */
export async function syncOtaChannel(flagValue) {
  if (flagValue !== true) return false;

  // `VUE_APP_OTA_CHANNEL` is the answer. Set it explicitly for every build that
  // should receive updates — including the store binary, whose .env comes from
  // S3 rather than this repo.
  //
  // The fallback below exists only so that forgetting it does not silently
  // strand the whole fleet, and it is deliberately limited to production
  // builds: applied everywhere, a colleague's debug build would sign in, assign
  // itself the members' channel, and have its local dist replaced by the
  // production bundle at the next background — a local-dev ghost that is very
  // hard to diagnose. A non-production build with no channel simply does not
  // subscribe.
  //
  // It is a safety net, not the design. Inferring "which members' code do I
  // run" from NODE_ENV — a variable about something else entirely, in a file
  // this repo does not control — is how a channel ends up wrong with nothing
  // reporting it. Hence the warning: the net caught you, go set the value.
  const isProductionBuild = process.env.VUE_APP_NODE_ENV === 'production';
  let channel = process.env.VUE_APP_OTA_CHANNEL;

  if (!channel && isProductionBuild) {
    channel = 'production';
    console.warn(
      '[OTA] VUE_APP_OTA_CHANNEL is not set — falling back to the "production" channel. ' +
        'Set it explicitly in the .env this build was compiled with.',
    );
  }

  if (!isOtaSupported() || !channel) return false;

  // Refuse to re-subscribe a device the kill switch has stopped. Checked here
  // rather than at the call site because every caller would otherwise have to
  // remember, and the one that forgets restarts the loop.
  //
  // The block only applies while the binary is the one that was killed. A store
  // release is a deliberate act by the same people who pulled the switch, and it
  // ships whatever fix the incident needed — so it clears the block without
  // anyone having to remember a flag.
  try {
    const killedAt = localStorage.getItem(KILLED_KEY);
    if (killedAt) {
      const { nativeVersion } = await getAppVersionForOta();
      if (killedAt === nativeVersion) {
        console.warn('[OTA] Live updates are killed on this device — not assigning a channel');
        return false;
      }
      localStorage.removeItem(KILLED_KEY);
      console.log('[OTA] Native version changed since the kill switch fired — updates re-enabled');
    }
  } catch {
    // localStorage unavailable: fall through and assign as normal.
  }

  // Both getChannel() and setChannel() are requests to the Capgo backend, and
  // the assignment already persists on the device — so the steady state used to
  // pay a round trip on every launch to learn nothing, competing with the
  // flags, profile and version-check fetches. Remember what we last assigned
  // and skip the calls entirely once it matches.
  try {
    if (localStorage.getItem(CHANNEL_SYNCED_KEY) === channel) {
      await verifyChannelAssignment(channel);
      return false;
    }

    // setChannel RESOLVES on failure — `ChannelRes` carries `status` and an
    // optional `error`, and the plugin does not throw for a refused assignment.
    // This used to be `await` and nothing else, so a refusal logged "Channel set
    // to …", wrote CHANNEL_SYNCED_KEY, and every later launch short-circuited on
    // that marker. A device that never got onto its channel is offered no bundle
    // by the backend, so the whole feature was off for it, permanently, silently.
    const result = await CapacitorUpdater.setChannel({ channel });
    if (result?.error) {
      console.warn(`[OTA] Channel assignment to "${channel}" was refused:`, result.error);
      reportOtaProblem('OTA channel assignment refused', {
        channel,
        status: result.status,
        reason: result.error,
      });
      // Deliberately NOT caching the marker: the next launch must try again.
      return false;
    }

    localStorage.setItem(CHANNEL_SYNCED_KEY, channel);
    console.log(`[OTA] Channel set to "${channel}"`);
    return true;
  } catch (error) {
    console.warn('[OTA] Could not set the update channel:', error?.message || error);
    reportOtaProblem('OTA channel assignment failed', {
      channel,
      reason: error?.message || String(error),
    });
    return false;
  }
}

/**
 * Check that the backend agrees with the channel we think we assigned.
 *
 * CHANNEL_SYNCED_KEY records what we last ASKED for, not what Capgo actually
 * has. The two diverge exactly when it matters: a device pinned to another
 * channel from the console, or by a build from a different lane. A dev-channel
 * pin is the common one — `dev` is targeted at non-prod builds, so it refuses to
 * serve a store install, and the device receives nothing at all while every
 * local signal says it is correctly assigned.
 *
 * ONLY runs when the device is still on the built-in bundle. A device already
 * running a downloaded bundle is demonstrably being served, so it pays no round
 * trip — which keeps this off the boot path for everyone whose OTA works.
 *
 * `allowSet: false` is the interesting answer: it means the device may not move
 * itself, so no amount of relaunching will fix it and someone has to clear the
 * override in the Capgo console. That is not recoverable from here, so it is
 * reported rather than retried.
 */
async function verifyChannelAssignment(expected) {
  const bundle = await getOtaBundle();
  if (!bundle || !bundle.isBuiltin) return;

  let current;
  try {
    current = await CapacitorUpdater.getChannel();
  } catch (error) {
    console.warn('[OTA] Could not read the assigned channel:', error?.message || error);
    return;
  }

  const actual = current?.channel;
  if (!actual || actual === expected) return;

  console.warn(`[OTA] Assigned to "${actual}" but this build belongs on "${expected}"`);
  reportOtaProblem('OTA device is on the wrong channel', {
    expected,
    actual,
    allowSet: current.allowSet,
  });

  // A device the backend will not let move has to be fixed in the console.
  if (current.allowSet === false) return;

  try {
    const result = await CapacitorUpdater.setChannel({ channel: expected });
    if (result?.error) {
      reportOtaProblem('OTA channel correction refused', {
        expected,
        actual,
        reason: result.error,
      });
      return;
    }
    localStorage.setItem(CHANNEL_SYNCED_KEY, expected);
    console.log(`[OTA] Corrected the channel from "${actual}" to "${expected}"`);
  } catch (error) {
    reportOtaProblem('OTA channel correction failed', {
      expected,
      actual,
      reason: error?.message || String(error),
    });
  }
}

/**
 * Close the gate again on sign-out, so a shared or handed-on device stops
 * receiving bundles until someone signs in.
 *
 * @returns {Promise<boolean>} true when the assignment was removed
 */
export async function clearOtaChannel() {
  if (!isOtaSupported()) return false;

  try {
    await CapacitorUpdater.unsetChannel({});
    localStorage.removeItem(CHANNEL_SYNCED_KEY);
    console.log('[OTA] Channel assignment cleared');
    return true;
  } catch (error) {
    console.warn('[OTA] Could not clear the update channel:', error?.message || error);
    return false;
  }
}

/**
 * Remote kill switch.
 *
 * Deliberately tri-state: only an explicit `false` reverts a device. An absent
 * or unknown flag leaves live updates alone, so an outage of the flags API
 * cannot mass-revert every install.
 *
 * @param {boolean|undefined} flagValue value of `ota_enabled`, or undefined
 * @returns {Promise<boolean>} true when a reset was performed
 */
export async function applyOtaKillSwitch(flagValue) {
  if (!isOtaSupported()) return false;

  // The flag turning back on has to lift the block, or a killed device stays
  // killed for good and only a store release recovers it.
  if (flagValue === true) {
    try {
      localStorage.removeItem(KILLED_KEY);
    } catch {
      // Nothing to do; the device simply stays blocked until storage works.
    }
  }

  if (flagValue !== false) return false;

  // Mark first, and mark even when already on the built-in bundle: the marker
  // is what stops the token watcher re-subscribing on the next launch, and a
  // device that has already reverted still needs that.
  try {
    const { nativeVersion } = await getAppVersionForOta();
    localStorage.setItem(KILLED_KEY, nativeVersion);
  } catch {
    // Best effort; the revert below still happens.
  }

  // Unassign before anything else, including before checking which bundle is
  // running. `autoUpdate` is native config, so a device that stays subscribed
  // re-downloads the killed bundle on the next foreground. A device already on
  // the built-in bundle needs this just as much — otherwise it fetches the
  // killed bundle once more and only that boot unassigns it.
  await clearOtaChannel();

  const bundle = await getOtaBundle();
  if (!bundle || bundle.isBuiltin) return false;

  try {
    console.warn('[OTA] ota_enabled is off — reverting to the built-in bundle');
    await CapacitorUpdater.reset({ toLastSuccessful: false });
    bundleLookup = null;
    return true;
  } catch (error) {
    console.warn('[OTA] Reset to the built-in bundle failed:', error?.message || error);
    return false;
  }
}
